import React, { useEffect, useState, JSX } from 'react';
import {
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  TableCell,
  TableRow,
  TextField,
  Box,
  Snackbar,
  Alert,
  CircularProgress,
  Typography,
  Divider,
} from '@mui/material';

import { Mql } from '@adalong/schemas';
import { IGroup, ICrawling, IUser, AdminApiTypes } from 'common';
import { Admin } from '../Admin';
import api from '../../services/api';
import { style } from 'typestyle';
import LocalizedStrings from 'react-localization';
import { ActionButton } from '../../components/ActionButton';
import { CrawlingEditDialog } from '../../components/CrawlingEditDialog';
import HashtagDeleteDialog from '../../components/HashtagDeleteDialog';
import { ADALONG_COLORS } from '../../styles';

type EditableGroup = Pick<
  IGroup,
  | 'name'
  | 'terms_url'
  | 'feature_flags'
  | 'feature_disabler_flag'
  | 'tags'
  | 'catalogCountries'
  | 'widgetMutedMediaFilter'
  | 'widgetExcludedMediaFilter'
> & { _id?: string };

type RuleField = 'widgetMutedMediaFilter' | 'widgetExcludedMediaFilter';

interface AudioRuleDialogProps {
  open: boolean;
  title: string;
  helperText: string;
  value?: Mql | null;
  onClose: () => void;
  onSave: (value: Mql | undefined) => void;
}

const RULE_PRESETS: Array<{ label: string; value: Mql }> = [
  {
    label: 'Music only',
    value: { labels: ['internal_music_v1.0'] },
  },
  {
    label: 'Music and voice',
    value: {
      and: [
        { labels: ['internal_music_v1.0'] },
        { labels: ['internal_voice_v1.0'] },
      ],
    },
  },
];

function AudioRuleDialog({
  open,
  title,
  helperText,
  value,
  onClose,
  onSave,
}: AudioRuleDialogProps) {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(value ? JSON.stringify(value, null, 2) : '');
    setError(null);
  }, [open, value]);

  const handleSave = () => {
    if (!draft.trim()) {
      onSave(undefined);
      return;
    }

    try {
      const parsed = JSON.parse(draft) as Mql;
      setDraft(JSON.stringify(parsed, null, 2));
      setError(null);
      onSave(parsed);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Invalid JSON');
    }
  };

  const applyPreset = (preset: Mql) => {
    setDraft(JSON.stringify(preset, null, 2));
    setError(null);
  };

  const formatDraft = () => {
    if (!draft.trim()) {
      setError(null);
      return;
    }

    try {
      const parsed = JSON.parse(draft);
      setDraft(JSON.stringify(parsed, null, 2));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Invalid JSON');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          bgcolor: ADALONG_COLORS.DARK_BLACK,
          color: ADALONG_COLORS.LIGHT_WHITE,
          borderRadius: '20px',
          border: `1px solid ${ADALONG_COLORS.LIGHT_BLACK}`,
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle
        sx={{
          color: ADALONG_COLORS.LIGHT_WHITE,
          fontSize: '1.5rem',
          fontWeight: 'bold',
          pt: 3,
          px: 4,
          mb: 0,
        }}
      >
        {title}
      </DialogTitle>
      <DialogContent sx={{ pt: '10px !important', pb: 3, px: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Typography variant="body2" sx={{ color: ADALONG_COLORS.SMOOTH_WHITE }}>
            {helperText}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {RULE_PRESETS.map((preset) => (
              <ActionButton
                key={preset.label}
                size="small"
                variant="blue"
                onAction={() => applyPreset(preset.value)}
              >
                {preset.label}
              </ActionButton>
            ))}
            <ActionButton size="small" variant="blue" onAction={formatDraft}>
              Format JSON
            </ActionButton>
            <ActionButton size="small" variant="transparent" onAction={() => setDraft('')}>
              Clear
            </ActionButton>
          </Box>
          <Divider sx={{ borderColor: ADALONG_COLORS.LIGHT_BLACK }} />
          <TextField
            multiline
            minRows={14}
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              if (error) {
                setError(null);
              }
            }}
            placeholder={`{\n  "labels": ["internal_music_v1.0"]\n}`}
            error={!!error}
            helperText={error || 'Leave empty to disable this rule.'}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                color: ADALONG_COLORS.LIGHT_WHITE,
                backgroundColor: ADALONG_COLORS.SMOOTHER_BLACK,
                borderRadius: '20px',
                fontFamily: 'monospace',
                '& fieldset': {
                  borderColor: ADALONG_COLORS.DARK_GRAY,
                },
                '&:hover fieldset': {
                  borderColor: ADALONG_COLORS.LIGHT_GRAY,
                },
                '&.Mui-focused fieldset': {
                  borderColor: ADALONG_COLORS.FLASH_BLUE,
                },
              },
              '& .MuiInputBase-input::placeholder': {
                color: ADALONG_COLORS.LIGHT_GRAY,
                opacity: 1,
              },
              '& .MuiFormHelperText-root': {
                color: error ? undefined : ADALONG_COLORS.SMOOTH_WHITE,
                ml: 0,
                mt: 1,
              },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 4, pb: 4, gap: 1 }}>
        <ActionButton variant="gray" onAction={onClose}>
          Cancel
        </ActionButton>
        <ActionButton variant="blue" onAction={handleSave}>
          Save rule
        </ActionButton>
      </DialogActions>
    </Dialog>
  );
}

interface GroupEditDialogProps {
  onValidItem: (group: Partial<EditableGroup> | null) => void;
  group: EditableGroup | null;
}

function GroupEditDialog(props: GroupEditDialogProps) {
  const [name, setName] = useState(props.group?.name || '');
  const defaultFeatureFlags: IGroup['feature_flags'] = {
    share: false,
    widget: false,
    upload_ba: false,
    create: false,
    product_catalog: false,
    text_media: false,
    analyze_dashboard: false,
    hide_old_ugc: false,
    hide_unavailable_ugc: false,
    crm: false,
    resize_thumbnails: false,
    audio_filters: false,
  };
  const initialFeatureFlags = {
    ...defaultFeatureFlags,
    ...props.group?.feature_flags,
  };
  const [featureFlags, setFeatureFlags] = useState(initialFeatureFlags);
  const defaultFeatureDisablerFlags: NonNullable<IGroup['feature_disabler_flag']> = {
    instagram_publish: false,
    influencer_whitelist: false,
    product_suggestions: true,
  };
  const initialFeatureDisablerFlags = {
    ...defaultFeatureDisablerFlags,
    ...props.group?.feature_disabler_flag,
  };
  const [featureDisablerFlags, setFeatureDisablerFlags] = useState(initialFeatureDisablerFlags);
  const [termsUrl, setTermsUrl] = useState(props.group?.terms_url || '');
  const [catalogCountries, setCatalogCountries] = useState<string[]>(props.group?.catalogCountries || []);
  const [newCatalogCountry, setNewCatalogCountry] = useState('');
  const [widgetMutedMediaFilter, setWidgetMutedMediaFilter] = useState<Mql | null | undefined>(
    props.group?.widgetMutedMediaFilter,
  );
  const [widgetExcludedMediaFilter, setWidgetExcludedMediaFilter] = useState<Mql | null | undefined>(
    props.group?.widgetExcludedMediaFilter,
  );
  const [editingRuleField, setEditingRuleField] = useState<RuleField | null>(null);

  const isValidUrl = (str: string | null): boolean => {
    if (!str) {
      return true;
    }
    try {
      new URL(str);
    } catch {
      return false;
    }
    return true;
  };

  const setGroupValidity = () => {
    if (name.length > 1 && isValidUrl(termsUrl)) {
      const newGroup = {
        ...props.group,
        name,
        feature_flags: featureFlags,
        feature_disabler_flag: featureDisablerFlags,
        terms_url: termsUrl,
        catalogCountries: catalogCountries,
        widgetMutedMediaFilter,
        widgetExcludedMediaFilter,
      };
      props.onValidItem(newGroup);
    } else {
      props.onValidItem(null);
    }
  };

  useEffect(setGroupValidity, [
    name,
    featureFlags,
    featureDisablerFlags,
    termsUrl,
    catalogCountries,
    widgetMutedMediaFilter,
    widgetExcludedMediaFilter,
  ]);

  const isNameEditingDisabled = !!props.group && props.group.name.length > 1;

  const changeFeatureFlag = (e: React.ChangeEvent<HTMLInputElement>, checked: boolean): void => {
    const ff = e.target.name;
    setFeatureFlags({
      ...featureFlags,
      [ff]: checked,
    });
  };

  const changeFeatureDisablerFlag = (e: React.ChangeEvent<HTMLInputElement>, checked: boolean): void => {
    const ff = e.target.name;
    setFeatureDisablerFlags({
      ...featureDisablerFlags,
      [ff]: checked,
    });
  };

  const handleAddCatalogCountry = () => {
    if (newCatalogCountry && !catalogCountries.includes(newCatalogCountry)) {
      setCatalogCountries([...catalogCountries, newCatalogCountry]);
      setNewCatalogCountry('');
    }
  };

  const handleRemoveCatalogCountry = (loc: string) => {
    setCatalogCountries(catalogCountries.filter((l) => l !== loc));
  };

  const ruleConfig: Record<
    RuleField,
    {
      label: string;
      helperText: string;
      value?: Mql | null;
      setter: (value: Mql | undefined) => void;
    }
  > = {
    widgetMutedMediaFilter: {
      label: 'Muted videos rule',
      helperText: 'Videos matching this rule will stay muted in the widget and cannot be unmuted.',
      value: widgetMutedMediaFilter,
      setter: setWidgetMutedMediaFilter,
    },
    widgetExcludedMediaFilter: {
      label: 'Excluded media rule',
      helperText: 'Media matching this rule will be excluded from the widget content response.',
      value: widgetExcludedMediaFilter,
      setter: setWidgetExcludedMediaFilter,
    },
  };

  const activeRule = editingRuleField ? ruleConfig[editingRuleField] : null;

  return (
    <>
      <Box className={form} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          type="text"
          value={name}
          label="Name"
          placeholder="adalong"
          disabled={isNameEditingDisabled}
          required={true}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '20px',
            },
          }}
        />
        <TextField
          type="text"
          value={termsUrl}
          label="Terms URL"
          placeholder="http://www.example.com/terms"
          onChange={(e) => setTermsUrl(e.target.value)}
          fullWidth
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '20px',
            },
          }}
        />
        <FormControl>
          <FormLabel>Feature Flags</FormLabel>
          <FormGroup>
            {Object.entries(featureFlags).sort(([a], [b]) => a.localeCompare(b)).map(([ff, checked]) => (
              <FormControlLabel
                key={ff}
                control={<Checkbox checked={checked} name={ff} onChange={changeFeatureFlag} />}
                label={lang.placeholder.featureFlags[ff as keyof IGroup['feature_flags']]}
              />
            ))}
          </FormGroup>
        </FormControl>
        <FormControl>
          <FormLabel>Feature Disabler Flags</FormLabel>
          <FormGroup>
            {Object.entries(featureDisablerFlags).sort(([a], [b]) => a.localeCompare(b)).map(([ff, checked]) => (
              <FormControlLabel
                key={ff}
                control={<Checkbox checked={checked} name={ff} onChange={changeFeatureDisablerFlag} />}
                label={lang.placeholder.featureDisablerFlags[ff as keyof NonNullable<IGroup['feature_disabler_flag']>]}
              />
            ))}
          </FormGroup>
        </FormControl>

        <FormControl>
          <FormLabel>Catalog countries</FormLabel>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            {catalogCountries.map((loc) => (
              <Chip key={loc} label={loc} onDelete={() => handleRemoveCatalogCountry(loc)} variant="outlined" />
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              value={newCatalogCountry}
              label="Add Catalog country"
              onChange={(e) => setNewCatalogCountry(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleAddCatalogCountry();
                  e.preventDefault();
                }
              }}
              size="small"
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '20px',
                },
              }}
            />
            <ActionButton variant="blue" onAction={handleAddCatalogCountry}>
              Add
            </ActionButton>
          </Box>
        </FormControl>

        <Divider />

        <Typography variant="h6" sx={{ color: ADALONG_COLORS.LIGHT_WHITE }}>
          Widget rules
        </Typography>

        {(Object.entries(ruleConfig) as [RuleField, typeof ruleConfig[RuleField]][]).map(
          ([field, config]) => (
            <Box
              key={field}
              sx={{
                p: 2,
                border: `1px solid ${ADALONG_COLORS.DARK_GRAY}`,
                borderRadius: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <Typography variant="subtitle2">{config.label}</Typography>
              <Typography variant="body2" color="text.secondary">
                {config.helperText}
              </Typography>
              <Typography variant="body2">
                {config.value ? 'Rule configured' : 'No rule configured'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <ActionButton variant="blue" onAction={() => setEditingRuleField(field)}>
                  Edit rule
                </ActionButton>
                <ActionButton
                  variant="transparent"
                  onAction={() => config.setter(undefined)}
                  disabled={!config.value}
                >
                  Clear rule
                </ActionButton>
              </Box>
            </Box>
          ),
        )}
      </Box>

      {activeRule ? (
        <AudioRuleDialog
          open={!!activeRule}
          title={activeRule.label}
          helperText={activeRule.helperText}
          value={activeRule.value}
          onClose={() => setEditingRuleField(null)}
          onSave={(value) => {
            activeRule.setter(value);
            setEditingRuleField(null);
          }}
        />
      ) : null}
    </>
  );
}

function Groups() {
  const [refreshIndex, setRefreshIndex] = useState(0);
  const [users, setUsers] = useState<IUser[]>([]);
  const [groups, setGroups] = useState<IGroup[]>([]);
  const [crawlings, setCrawlings] = useState<ICrawling[]>([]);
  const [crawlingDialogOpen, setCrawlingDialogOpen] = useState(false);
  const [editingCrawling, setEditingCrawling] = useState<ICrawling | null>(null);
  const [defaultHashtag, setDefaultHashtag] = useState('');
  const [defaultGroup, setDefaultGroup] = useState<IGroup | null>(null);
  const [isLoadingCrawling, setIsLoadingCrawling] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tagToDelete, setTagToDelete] = useState<string>('');
  const [groupForDeletion, setGroupForDeletion] = useState<EditableGroup | null>(null);

  useEffect(() => {
    // Fetch users, groups, and crawlings for crawling creation/editing
    // This happens once when the component mounts
    Promise.all([
      api.getUsers({
        filters: { name: '' },
        options: { limit: 1000, skip: 0 }
      }),
      api.getGroups({
        filters: { name: '' },
        options: { limit: 1000, skip: 0 }
      }),
      api.getCrawlings('')
    ]).then(([usersRes, groupsRes, crawlingsRes]) => {
      setUsers(usersRes.users);
      setGroups(groupsRes.groups);
      setCrawlings(crawlingsRes);
    }).catch((e) => {
      console.error('Error loading groups/users/crawlings:', e);
    });
  }, []);

  const showSnackbar = (message: string) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const displayHeader = (
    <TableRow>
      <TableCell style={{ minWidth: 80 }}>Name</TableCell>
      <TableCell>Tags</TableCell>
      <TableCell style={{ minWidth: 100 }}>Add #</TableCell>
      <TableCell style={{ minWidth: 70 }} />
    </TableRow>
  );

  const update = async (group: Partial<EditableGroup>): Promise<void> => {
    if (!group._id) {
      return Promise.reject(new Error('Cannot update group without an _id'));
    }
    const payload = toPayload(group);
    await api.updateGroup(group._id, payload);
  };

  const addTagToGroup = async (tag: string, group: EditableGroup) => {
    // Don't add the tag yet - only open the dialog for creation
    // Tag will be added when user clicks Save in the dialog
    setDefaultHashtag(tag);
    setDefaultGroup(group as any);
    setEditingCrawling(null); // null = creation mode
    setCrawlingDialogOpen(true);
  };

  const handleSaveCrawling = async (crawling: Partial<ICrawling>): Promise<void> => {
    const isCreating = !editingCrawling;
    
    // If creating, add the tag to the group first
    if (isCreating && defaultGroup) {
      await update({
        ...defaultGroup,
        tags: [...(defaultGroup.tags || []), defaultHashtag],
      });
    }
    
    // Save the crawling (create or update)
    const payload = {
      ...crawling,
      owner: crawling.owner?._id,
      group: crawling.group?._id,
    };
    await api.addOrUpdateCrawling(payload as Partial<ICrawling>);
    
    // Refresh the crawlings list
    const updatedCrawlings = await api.getCrawlings('');
    setCrawlings(updatedCrawlings);
    
    // Refresh the groups list
    setRefreshIndex(refreshIndex + 1);
  };

  const handleCloseCrawlingDialog = () => {
    setCrawlingDialogOpen(false);
    setDefaultHashtag('');
    setDefaultGroup(null);
    setEditingCrawling(null);
  };

  const handleEditCrawling = async (tag: string, group: EditableGroup) => {
    // When user clicks a hashtag pill, find the matching crawling
    setIsLoadingCrawling(true);
    
    try {
      const matchingCrawling = crawlings.find(
        (c) => c.hashtag === tag && c.group?._id === group._id
      );
      
      if (matchingCrawling) {
        setEditingCrawling(matchingCrawling);
        setDefaultHashtag('');
        setDefaultGroup(null);
        setCrawlingDialogOpen(true);
      } else {
        showSnackbar(`No crawling found for hashtag "${tag}". This tag exists in the group but has no associated crawling.`);
      }
    } catch (e) {
      console.error('Error fetching crawling:', e);
      showSnackbar('Error loading crawling data');
    } finally {
      setIsLoadingCrawling(false);
    }
  };

  const removeTagFromGroup = async (tag: string, group: EditableGroup) => {
    // Show the confirmation dialog
    setTagToDelete(tag);
    setGroupForDeletion(group);
    setDeleteDialogOpen(true);
  };

  const handleDeleteBoth = async () => {
    if (!groupForDeletion || !tagToDelete) return;
    
    // Delete both the crawling and the tag from the group
    try {
      const matchingCrawling = crawlings.find(
        (c) => c.hashtag === tagToDelete && c.group?._id === groupForDeletion._id
      );
      
      if (matchingCrawling) {
        await api.deleteCrawling(matchingCrawling);
      }
    } catch (e) {
      console.error('Error deleting crawling:', e);
    }
    
    // Remove the tag from the group
    await update({
      ...groupForDeletion,
      tags: groupForDeletion.tags.filter((t) => t !== tagToDelete),
    });
    
    // Refresh the crawlings list
    const updatedCrawlings = await api.getCrawlings('');
    setCrawlings(updatedCrawlings);
    
    setRefreshIndex(refreshIndex + 1);
    setDeleteDialogOpen(false);
    setTagToDelete('');
    setGroupForDeletion(null);
  };

  const handleDeleteCrawlingOnly = async () => {
    if (!groupForDeletion || !tagToDelete) return;
    
    // Only delete the crawling, keep the tag in the group
    try {
      const matchingCrawling = crawlings.find(
        (c) => c.hashtag === tagToDelete && c.group?._id === groupForDeletion._id
      );
      
      if (matchingCrawling) {
        await api.deleteCrawling(matchingCrawling);
      }
    } catch (e) {
      console.error('Error deleting crawling:', e);
    }
    
    // Refresh the crawlings list
    const updatedCrawlings = await api.getCrawlings('');
    setCrawlings(updatedCrawlings);
    
    setRefreshIndex(refreshIndex + 1);
    setDeleteDialogOpen(false);
    setTagToDelete('');
    setGroupForDeletion(null);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setTagToDelete('');
    setGroupForDeletion(null);
  };

  const create = async (group: Partial<EditableGroup>): Promise<void> => {
    const payload = toPayload(group);
    await api.createGroup(payload);
  };

  const search = (input: string): Promise<EditableGroup[]> => {
    return api.getGroups({ filters: { name: input }, options: { limit: 1000, skip: 0 } }).then((res) => res.groups);
  };

  const remove = (group: EditableGroup): Promise<void> => {
    if (!group._id) {
      return Promise.reject(new Error('Cannot delete group without an _id'));
    }
    return api.deleteGroup(group._id);
  };

  const hasCrawling = (tag: string, group: EditableGroup): boolean => {
    return crawlings.some(
      (c) => c.hashtag === tag && c.group?._id === group._id
    );
  };

  const displayLine = (group: EditableGroup): JSX.Element[] => {
    return [
      <TableCell key={group._id}>{group.name}</TableCell>,
      <TableCell key={group._id + '_tag'}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
          {(group.tags ?? []).map((tag, index) => {
            const handleDelete = (e: React.MouseEvent) => {
              e.stopPropagation();
              removeTagFromGroup(tag, group);
            };
            const handleClick = (e: React.MouseEvent) => {
              const target = e.target as HTMLElement;
              const isDeleteIcon = target.closest('.MuiChip-deleteIcon');
              
              if (!isDeleteIcon) {
                handleEditCrawling(tag, group);
              }
            };
            const hasAssociatedCrawling = hasCrawling(tag, group);
            return (
              <Chip 
                key={`${group._id}_${tag}_${index}`} 
                label={tag} 
                onDelete={handleDelete} 
                onClick={handleClick}
                variant="outlined" 
                size="small"
                sx={{
                  cursor: 'pointer',
                  borderColor: hasAssociatedCrawling ? undefined : '#8d0000',
                  '&:hover': {
                    bgcolor: ADALONG_COLORS.LIGHT_BLACK,
                  },
                }}
              />
            );
          })}
        </Box>
      </TableCell>,
      <TableCell key={group._id + '_input'}>
        <TextField
          label="hashtag"
          variant="outlined"
          size="small"
          onKeyPress={(e) => {
            if (e.charCode == 13) {
              const input = e.currentTarget.querySelector('input');
              if (input) {
                addTagToGroup(input.value, group);
                input.value = '';
              }
            }
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '20px',
            },
          }}
        />
      </TableCell>,
    ];
  };

  const displayEditWindow = (
    group: EditableGroup | null,
    onValidItem: (group: Partial<EditableGroup> | null) => void
  ): JSX.Element => {
    return <GroupEditDialog onValidItem={onValidItem} group={group} />;
  };

  const customSection = (
    <Box sx={{ mb: 3, p: 2, bgcolor: ADALONG_COLORS.LIGHT_BLACK, borderRadius: '10px' }}>
      <Typography variant="body2" sx={{ color: ADALONG_COLORS.LIGHT_WHITE, mb: 1 }}>
        Hashtags that appear in Groups are used as filters in Discover.
      </Typography>
      <Typography variant="body2" sx={{ color: ADALONG_COLORS.LIGHT_WHITE, mb: 1 }}>
        When you add a hashtag filter in Groups, a corresponding crawling is automatically created.
      </Typography>
      <Typography variant="body2" sx={{ color: ADALONG_COLORS.LIGHT_WHITE, mb: 1 }}>
        If you'd like to remove it, you have two options:
      </Typography>
      <Typography variant="body2" component="ul" sx={{ color: ADALONG_COLORS.LIGHT_WHITE, pl: 3, mb: 0 }}>
        <li>Delete both the crawling and the hashtag filter, or</li>
        <li>Delete only the crawling.</li>
      </Typography>
    </Box>
  );

  return (
    <>
      <Admin<EditableGroup>
        title="Groups"
        createButtonLabel="Add"
        displayHeader={displayHeader}
        create={create}
        update={update}
        search={search}
        remove={remove}
        refreshIndex={refreshIndex}
        getId={(group) => group._id!}
        displayLine={displayLine}
        displayEditWindow={displayEditWindow}
        customSection={customSection}
      />
      {crawlingDialogOpen && (
        <CrawlingEditDialog
          open={crawlingDialogOpen}
          onClose={handleCloseCrawlingDialog}
          crawling={editingCrawling}
          groups={groups}
          users={users}
          onSave={handleSaveCrawling}
          defaultHashtag={defaultHashtag}
          defaultGroup={defaultGroup || undefined}
        />
      )}
      {isLoadingCrawling && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0, 0, 0, 0.3)',
            zIndex: 9999,
          }}
        >
          <CircularProgress size={60} />
        </Box>
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity="warning" 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      {deleteDialogOpen && (
        <HashtagDeleteDialog
          open={deleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          onDeleteBoth={handleDeleteBoth}
          onDeleteCrawlingOnly={handleDeleteCrawlingOnly}
        />
      )}
    </>
  );
}

const toPayload = (g: Partial<EditableGroup>): AdminApiTypes.BodyCreateGroup => {
  return {
    name: g.name!,
    termsUrl: g.terms_url,
    featureFlags: g.feature_flags,
    featureDisablerFlags: g.feature_disabler_flag,
    tags: g.tags || [],
    catalogCountries: g.catalogCountries || [],
    widgetMutedMediaFilter: g.widgetMutedMediaFilter ?? null,
    widgetExcludedMediaFilter: g.widgetExcludedMediaFilter ?? null,
  };
};

export default Groups;

const form = style({ display: 'flex', flexDirection: 'column', gap: 10 });

const lang = new LocalizedStrings({
  en: {
    placeholder: {
      featureFlags: {
        share: 'Give access to Share module',
        widget: 'Give access to Widget module',
        upload_ba: 'Give access to Upload Brand Assets module',
        create: 'Give access to Create module',
        product_catalog: 'Give access to Product Filter, Product Linking and Product Widget features',
        text_media: 'Can scrape and display plain text Tweets',
        hide_old_ugc: 'Hide old UGC(> 1 year) from widgets',
        hide_unavailable_ugc: 'Hide unavailable UGC from widgets',
        crm: 'CRM',
        analyze_dashboard: 'Give access to new Analyze dashboard',
        resize_thumbnails: 'Resize thumbnails',
        audio_filters: 'Enable audio filters (music / voice)',
      },
      featureDisablerFlags: {
        instagram_publish: 'Hide Instagram Publish',
        influencer_whitelist: 'Hide Influencer Whitelist',
        product_suggestions: 'Hide Product Suggestions',
      },
    },
  },
});
